import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User } from '../api/supabaseEntities.js';
import { Company } from '../api/supabaseEntities.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Badge } from '../components/ui/badge.jsx';
import { Switch } from '../components/ui/switch.jsx';
import { Separator } from '../components/ui/separator.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import { Checkbox } from '../components/ui/checkbox.jsx';
import { useToast } from '../hooks/useToast.js';
import { ArrowLeft, User as UserIcon, Shield, Settings, Save, X } from 'lucide-react';
import { createPageUrl } from '../utils/index.ts';

const ROLE_LABELS = {
  admin: 'Administrador',
  client: 'Cliente',
  support: 'Suporte',
  developer: 'Desenvolvedor',
  manager: 'Gestor'
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  blocked: 'bg-red-100 text-red-800'
};

const STATUS_LABELS = {
  active: 'Ativo',
  inactive: 'Inativo',
  blocked: 'Bloqueado'
};

const DEFAULT_PERMISSIONS = {
  tickets: {
    view: true,
    create: true,
    edit: false,
    delete: false,
    assign: false,
    change_status: false
  },
  projects: {
    view: false,
    create: false,
    edit: false,
    delete: false
  },

  reports: {
    view: false,
    export: false
  },
  users: {
    view: false,
    create: false,
    edit: false,
    delete: false
  }
};

export default function EditUser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');
  const [isLoading, setIsLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'client',
    department: '',
    position: '',
    company_id: '',
    status: 'active',
    permissions: DEFAULT_PERMISSIONS
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('🔄 Carregando dados do usuário para edição...');
      
      // Carregar usuário atual para verificar permissões
      const userData = await User.me();
      setCurrentUser(userData);

      if (!userData || userData.role !== 'admin') {
        navigate(createPageUrl('Dashboard'));
        return;
      }

      // Carregar empresas
      try {
        const companiesData = await Company.list();
        setCompanies(companiesData || []);
        console.log('✅ Empresas carregadas:', companiesData?.length || 0);
      } catch (companyError) {
        console.warn('⚠️ Erro ao carregar empresas:', companyError);
        setCompanies([]);
      }

      // Carregar dados do usuário a ser editado usando dados reais
      if (userId) {
        try {
          console.log('🔍 Buscando usuário ID:', userId);
          const userToEdit = await User.findById(userId);
          
          if (!userToEdit) {
            setError('Usuário não encontrado');
            return;
          }
          
          console.log('✅ Usuário encontrado:', userToEdit);
          console.log('🔍 Campo phone específico:', userToEdit.phone);
          console.log('🔍 Todos os campos do usuário:', Object.keys(userToEdit));
          
          // Mapear dados do usuário para o formato do formulário
          setFormData({
            name: userToEdit.name || '',
            email: userToEdit.email || '',
            phone: userToEdit.phone || '',
            role: userToEdit.role || 'client',
            company_id: userToEdit.company_id || '',
            // Campos que podem não existir na estrutura atual
            department: userToEdit.department || '',
            position: userToEdit.position || '',
            status: userToEdit.status || 'active',
            // Permissões padrão (sistema de permissões pode ser implementado futuramente)
            permissions: {
              ...DEFAULT_PERMISSIONS,
              ...(userToEdit.permissions || {})
            }
          });
        } catch (userError) {
          console.error('❌ Erro ao buscar usuário:', userError);
          setError('Erro ao carregar dados do usuário');
          return;
        }
      } else {
        // Novo usuário - usar dados padrão
        console.log('➕ Preparando formulário para novo usuário');
        setFormData({
          name: '',
          email: '',
          phone: '',
          role: 'client',
          company_id: '',
          department: '',
          position: '',
          status: 'active',
          permissions: DEFAULT_PERMISSIONS
        });
      }
    } catch (error) {
      console.error('❌ Erro geral ao carregar dados:', error);
      setError('Erro ao carregar dados. Por favor, tente novamente.');
    }
    setIsLoading(false);
  };

  const handlePermissionChange = (category, permission, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [category]: {
          ...prev.permissions[category],
          [permission]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('💾 Salvando dados do usuário:', formData);
      
      // Preparar dados para envio
      const { permissions, ...userData } = formData;
      const dataToSend = {
        ...userData,
        // Mapear para estrutura esperada
        type: formData.role
      };

      if (userId) {
        // Atualizar usuário existente
        console.log('🔄 Atualizando usuário ID:', userId);
        await User.update(userId, dataToSend);
        console.log('✅ Usuário atualizado com sucesso');
        alert('Usuário atualizado com sucesso!');
      } else {
        // Criar novo usuário
        console.log('➕ Criando novo usuário');
        await User.create(dataToSend);
        console.log('✅ Usuário criado com sucesso');
        alert('Usuário criado com sucesso!');
      }

      navigate(createPageUrl('Users'));
      
    } catch (error) {
      console.error('❌ Erro ao salvar usuário:', error);
      alert('Erro ao salvar usuário. Por favor, tente novamente.');
    }

    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold">Erro ao Carregar Dados</h2>
        <p className="text-muted-foreground mt-2">{error}</p>
        <Button 
          onClick={() => navigate(createPageUrl('Users'))}
          className="mt-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Usuários
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Users'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {userId ? 'Editar Usuário' : 'Novo Usuário'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {userId ? 'Edite as informações do usuário' : 'Crie um novo usuário no sistema'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="permissions">Permissões</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                Dados do Usuário
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome Completo</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone/WhatsApp</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Tipo de Usuário</Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                    >
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.role === 'client' && (
                    <div className="space-y-2">
                      <Label htmlFor="company">Empresa</Label>
                      <select
                        id="company"
                        value={formData.company_id || ''}
                        onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      >
                        <option value="">Selecione uma empresa</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name || company.corporate_name || 'Empresa sem nome'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="department">Departamento</Label>
                    <Input
                      id="department"
                      value={formData.department || ''}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="position">Cargo</Label>
                    <Input
                      id="position"
                      value={formData.position || ''}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(createPageUrl('Users'))}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Permissões do Usuário
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Chamados */}
                <div>
                  <h3 className="font-medium mb-4">Chamados</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label>Visualizar chamados</Label>
                      <Switch
                        checked={formData.permissions.tickets.view}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Criar chamados</Label>
                      <Switch
                        checked={formData.permissions.tickets.create}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'create', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Editar chamados</Label>
                      <Switch
                        checked={formData.permissions.tickets.edit}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'edit', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Excluir chamados</Label>
                      <Switch
                        checked={formData.permissions.tickets.delete}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'delete', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Atribuir chamados</Label>
                      <Switch
                        checked={formData.permissions.tickets.assign}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'assign', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Alterar status</Label>
                      <Switch
                        checked={formData.permissions.tickets.change_status}
                        onCheckedChange={(checked) => handlePermissionChange('tickets', 'change_status', checked)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Projetos */}
                <div>
                  <h3 className="font-medium mb-4">Projetos</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label>Visualizar projetos</Label>
                      <Switch
                        checked={formData.permissions.projects.view}
                        onCheckedChange={(checked) => handlePermissionChange('projects', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Criar projetos</Label>
                      <Switch
                        checked={formData.permissions.projects.create}
                        onCheckedChange={(checked) => handlePermissionChange('projects', 'create', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Editar projetos</Label>
                      <Switch
                        checked={formData.permissions.projects.edit}
                        onCheckedChange={(checked) => handlePermissionChange('projects', 'edit', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Excluir projetos</Label>
                      <Switch
                        checked={formData.permissions.projects.delete}
                        onCheckedChange={(checked) => handlePermissionChange('projects', 'delete', checked)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Relatórios */}
                <div>
                  <h3 className="font-medium mb-4">Relatórios</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label>Visualizar relatórios</Label>
                      <Switch
                        checked={formData.permissions.reports.view}
                        onCheckedChange={(checked) => handlePermissionChange('reports', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Exportar relatórios</Label>
                      <Switch
                        checked={formData.permissions.reports.export}
                        onCheckedChange={(checked) => handlePermissionChange('reports', 'export', checked)}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Usuários */}
                <div>
                  <h3 className="font-medium mb-4">Usuários</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex items-center justify-between">
                      <Label>Visualizar usuários</Label>
                      <Switch
                        checked={formData.permissions.users.view}
                        onCheckedChange={(checked) => handlePermissionChange('users', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Criar usuários</Label>
                      <Switch
                        checked={formData.permissions.users.create}
                        onCheckedChange={(checked) => handlePermissionChange('users', 'create', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Editar usuários</Label>
                      <Switch
                        checked={formData.permissions.users.edit}
                        onCheckedChange={(checked) => handlePermissionChange('users', 'edit', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Excluir usuários</Label>
                      <Switch
                        checked={formData.permissions.users.delete}
                        onCheckedChange={(checked) => handlePermissionChange('users', 'delete', checked)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(createPageUrl('Users'))}
                >
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
